import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// ========================= Types =========================

/**
 * WHO the tax is remitted to. Not cosmetic — these are different governments,
 * with different returns, on different schedules. A component filed under the
 * wrong authority is money collected for one government and paid to another.
 */
export type TaxAuthority = "CRA" | "REVENU_QUEBEC";

export const AUTHORITY_LABEL: Record<TaxAuthority, string> = {
  CRA: "Canada Revenue Agency",
  REVENU_QUEBEC: "Revenu Québec",
};

/**
 * ONE COMPONENT of the tax on a sale — not "the tax rate".
 *
 * A province is not one number. Québec is 5% GST (to the CRA) PLUS 9.975% QST (to
 * Revenu Québec): two governments, two returns, two invoice lines. A single
 * fraction gets the customer's total right and then remits the wrong amounts to
 * the wrong places.
 */
export interface TaxRate {
  id: string;
  province: string; // CA province/territory code, e.g. "ON"
  authority: TaxAuthority;
  code: string; // "GST" | "HST" | "QST"
  fraction: number | string; // 0.13 = 13% (Decimal arrives as a string)
  label?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertTaxRateInput {
  province: string;
  authority: TaxAuthority;
  code: string;
  fraction: number;
  label?: string;
  active?: boolean;
}

/** `province` + `code` identify WHICH component — they are not editable content. */
export type UpdateTaxRateInput = Partial<
  Omit<UpsertTaxRateInput, "province" | "code">
>;

// ========================= Hooks =========================

export function useGetTaxRates() {
  const { token } = useSession();
  return useQuery({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data } = await axios.get<{ data: TaxRate[] }>(
        `${API_URL}/api/v2/admin/tax-rates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

/** Upsert by province (create or replace the row for that province). */
export function useUpsertTaxRate(options?: {
  onSuccess?: (data: TaxRate) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertTaxRateInput) => {
      const { data } = await axios.post<{ data: TaxRate }>(
        `${API_URL}/api/v2/admin/tax-rates`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["tax-rates"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateTaxRate(options?: {
  onSuccess?: (data: TaxRate) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateTaxRateInput;
    }) => {
      const { data } = await axios.patch<{ data: TaxRate }>(
        `${API_URL}/api/v2/admin/tax-rates/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["tax-rates"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteTaxRate(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/tax-rates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tax-rates"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
